import { useEffect, useMemo, useRef, useState } from "react";

import Modal from "../../../components/Modal/Modal";
import DateInput from "../../../components/input/DateInput/DateInput";
import TextInput from "../../../components/input/TextInput";
import SelectInput from "../../../components/input/SelectInput/SelectInput";
import { formatDate, getUtcOffset } from "../../../utils/functions";
import {
	runExportLicensesCsv,
	runGetCatalogs,
	runGetLicense,
	runUpdateBulkLicenses,
	runUpdateLicense,
} from "../../../utils/services";
import styles from "./Search.module.scss";

const formatFileSize = (bytes = 0) => {
	if (!bytes || Number.isNaN(bytes)) return "0 B";
	if (bytes < 1024) return `${bytes} B`;
	if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
	return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const defaultFilters = {
	company: "",
	serial: "",
	deviceModel: "",
	startDate: "",
	endDate: "",
};

const emptyEditForm = {
	company: "",
	taxId: "",
	contactName: "",
	position: "",
	phone: "",
	city: "",
	state: "",
	address: "",
	email: "",
	serial: "",
	purchaseDate: "",
	expirationDate: "",
	type: "",
	deviceModel: "",
	exception: "false",
	status: "",
};

const EXCEPTION_DEVICE_MODEL = "MSO1300E2";

const editFieldLabels = {
	company: "Empresa",
	taxId: "NIT",
	contactName: "Nombre de contacto",
	position: "Cargo",
	phone: "Telefono",
	city: "Ciudad",
	state: "Departamento",
	address: "Direccion",
	email: "Correo",
};

let cachedDeviceModels = [];
let cachedLicenseTypes = [];

const translateSearchCode = (code, fallbackMessage = "") => {
	switch (code) {
		case "LRG001":
			return "";
		case "LRG002":
			return fallbackMessage || "Fechas requeridas.";
		case "LRT008":
			return "Modelo no valido.";
		case "LRT004":
			return "Licencia no encontrada.";
		case "LRT009":
			return "No se permiten esos cambios.";
		case "LRL008":
			return "Su sesion ha expirado.";
		default:
			return fallbackMessage || "No se pudo consultar la licencia.";
	}
};

const Search = () => {
	const [filters, setFilters] = useState(defaultFilters);
	const [currentPage, setCurrentPage] = useState(1);
	const [totalPages, setTotalPages] = useState(1);
	const [totalItems, setTotalItems] = useState(0);
	const [isLoading, setIsLoading] = useState(false);
	const [lookupError, setLookupError] = useState("");
	const [licenseRows, setLicenseRows] = useState([]);
	const [copiedSerial, setCopiedSerial] = useState("");
	const [deviceModels, setDeviceModels] = useState([]);
	const [licenseTypes, setLicenseTypes] = useState([]);
	const [isLoadingCatalogs, setIsLoadingCatalogs] = useState(false);
	const [editModalOpen, setEditModalOpen] = useState(false);
	const [selectedRow, setSelectedRow] = useState(null);
	const [originalRowData, setOriginalRowData] = useState(null);
	const [editFormData, setEditFormData] = useState(emptyEditForm);
	const [isExportingCsv, setIsExportingCsv] = useState(false);
	const [isUpdatingCsv, setIsUpdatingCsv] = useState(false);
	const [csvModalOpen, setCsvModalOpen] = useState(false);
	const [csvFile, setCsvFile] = useState(null);
	const [csvUploadSummary, setCsvUploadSummary] = useState(null);
	const [csvFeedback, setCsvFeedback] = useState(null);
	const updateCsvInputRef = useRef(null);

	const buildLicenseTypeLabel = (value = "") => {
		const licenseType = licenseTypes.find((item) => item.value === value);
		return licenseType?.name || value || "-";
	};

	useEffect(() => {
		if (cachedDeviceModels.length > 0 || cachedLicenseTypes.length > 0) {
			setDeviceModels(cachedDeviceModels);
			setLicenseTypes(cachedLicenseTypes);
			return;
		}

		let isMounted = true;

		const loadCatalogs = async () => {
			setIsLoadingCatalogs(true);

			try {
				const response = await runGetCatalogs();
				const catalogs = response?.message || {};

				if (!isMounted) return;

				cachedDeviceModels = catalogs.deviceModels || [];
				cachedLicenseTypes = catalogs.typeLicenses || [];
				setDeviceModels(cachedDeviceModels);
				setLicenseTypes(cachedLicenseTypes);
			} catch (error) {
				if (!isMounted) return;
				setLookupError(
					error?.code === "LRL008"
						? "Su sesion ha expirado."
						: error?.message ||
								"No se pudieron cargar los catalogos de licencias.",
				);
			} finally {
				if (isMounted) {
					setIsLoadingCatalogs(false);
				}
			}
		};

		loadCatalogs();

		return () => {
			isMounted = false;
		};
	}, []);

	const deviceModelOptions = useMemo(
		() =>
			deviceModels.map((deviceModel) => ({
				value: deviceModel.value,
				label: deviceModel.name,
			})),
		[deviceModels],
	);

	const editDeviceModelOptions = useMemo(
		() =>
			deviceModels.map((deviceModel) => ({
				value: deviceModel.value,
				label: deviceModel.name,
			})),
		[deviceModels],
	);

	const exceptionOptions = [
		{ value: "false", label: "No aplica" },
		{ value: "true", label: "Aplica" },
	];
	const statusOptions = [
		{ value: "", label: "Seleccione un estado" },
		{ value: "active", label: "Activo" },
		{ value: "expired", label: "Expirado" },
		{ value: "revoked", label: "Revocado" },
	];
	const isEditExceptionEnabled =
		editFormData.deviceModel === EXCEPTION_DEVICE_MODEL;

	const buildQuery = () =>
		Object.fromEntries(
			Object.entries(filters).filter(([, value]) => Boolean(value)),
		);

	const fetchLicenses = async (pageNumber = 1) => {
		setIsLoading(true);
		setLookupError("");
		setLicenseRows([]);

		try {
			const response = await runGetLicense({
				page: pageNumber,
				limit: 20,
				...buildQuery(),
			});
			const rows = response?.message?.licenses ?? [];
			const pagination = response?.message?.pagination ?? {};

			setLicenseRows(rows);
			setCurrentPage(pagination.page || pageNumber);
			setTotalPages(pagination.pages || 1);
			setTotalItems(pagination.total || rows.length);
			setCopiedSerial("");

			if (rows.length === 0) {
				setLookupError(
					"No se encontraron licencias con los filtros enviados.",
				);
			}
		} catch (error) {
			setLookupError(
				translateSearchCode(
					error?.code,
					error?.message || error?.message,
				),
			);
		} finally {
			setIsLoading(false);
		}
	};

	const handleFilterChange = (event) => {
		const { name, value } = event.target;

		setFilters((current) => ({
			...current,
			[name]: value,
		}));
		setLookupError("");
	};

	const handleSearch = async (event) => {
		event.preventDefault();
		await fetchLicenses(1);
	};

	const handleCopyLicense = async (license, serial) => {
		if (!license) return;

		try {
			await navigator.clipboard.writeText(license);
			setCopiedSerial(serial);
			window.setTimeout(() => setCopiedSerial(""), 1500);
		} catch {
			setLookupError("No se pudo copiar la licencia.");
		}
	};

	const normalizeEditRow = (row) => ({
		company: row.company || "",
		taxId: row.taxId || "",
		contactName: row.contactName || "",
		position: row.position || "",
		phone: row.phone || "",
		city: row.city || "",
		state: row.state || "",
		address: row.address || "",
		email: row.email || "",
		serial: row.serial || "",
		purchaseDate: row.purchaseDate ? row.purchaseDate.slice(0, 10) : "",
		expirationDate: row.expirationDate
			? row.expirationDate.slice(0, 10)
			: "",
		type: row.type?.value || row.type || "",
		deviceModel: row.deviceModel?.value || row.deviceModel || "",
		exception: row.exception ? "true" : "false",
		status: row.status || "",
	});

	const buildEditPayload = (currentData, originalData) => {
		const payload = Object.keys(currentData).reduce(
			(currentPayload, key) => {
				if (currentData[key] !== originalData[key]) {
					currentPayload[key] =
						key === "exception"
							? currentData[key] === "true"
							: currentData[key];
				}

				return currentPayload;
			},
			{},
		);

		if (
			currentData.deviceModel !== EXCEPTION_DEVICE_MODEL &&
			originalData.exception === "true"
		) {
			payload.exception = false;
		}

		return payload;
	};

	const handleModifyRow = (row) => {
		const normalizedRow = normalizeEditRow(row);

		setSelectedRow(row);
		setOriginalRowData(normalizedRow);
		setEditFormData(normalizedRow);
		setEditModalOpen(true);
		setLookupError("");
	};

	const handleEditFieldChange = (event) => {
		const { name, value } = event.target;

		setEditFormData((current) => {
			const updatedData = {
				...current,
				[name]: value,
			};

			if (name === "deviceModel" && value !== EXCEPTION_DEVICE_MODEL) {
				updatedData.exception = "false";
			}

			return updatedData;
		});
	};

	const handleCloseEditModal = () => {
		setEditModalOpen(false);
		setSelectedRow(null);
		setOriginalRowData(null);
		setEditFormData(emptyEditForm);
	};

	const handleConfirmEdit = async (event) => {
		event.preventDefault();

		if (!selectedRow?._id) {
			setLookupError("Licencia no valida.");
			return;
		}

		const payload = buildEditPayload(
			editFormData,
			originalRowData || emptyEditForm,
		);

		if (Object.keys(payload).length === 0) {
			setLookupError("Sin cambios.");
			return;
		}

		try {
			await runUpdateLicense(selectedRow._id, {
				...payload,
				utcOffset: getUtcOffset(),
			});
			handleCloseEditModal();
			await fetchLicenses(currentPage);
		} catch (error) {
			setLookupError(
				translateSearchCode(error?.code, error?.message || ""),
			);
		}
	};

	const goToPage = async (pageNumber) => {
		if (pageNumber < 1 || pageNumber > totalPages || isLoading) return;
		await fetchLicenses(pageNumber);
	};

	const handleExportCsv = async () => {
		setIsExportingCsv(true);
		setCsvFeedback(null);

		try {
			const response = await runExportLicensesCsv();
			const csvContent =
				typeof response === "string"
					? response
					: response?.data || response?.message || "";

			if (!csvContent) {
				setCsvFeedback({
					type: "error",
					message:
						"No se pudo descargar el archivo CSV de licencias.",
				});
				return;
			}

			const blob = new Blob([csvContent], {
				type: "text/csv;charset=utf-8",
			});
			const url = window.URL.createObjectURL(blob);
			const link = document.createElement("a");
			const downloadDate = new Date().toISOString().split("T")[0];

			link.href = url;
			link.download = `licenses-export_${downloadDate}.csv`;
			link.click();
			window.URL.revokeObjectURL(url);

			setCsvFeedback({
				type: "success",
				message: "Archivo CSV de licencias exportado exitosamente.",
			});
		} catch (error) {
			setCsvFeedback({
				type: "error",
				message:
					error?.code === "LRL008"
						? "Su sesion ha expirado."
						: error?.message ||
							"No se pudo exportar el archivo CSV.",
			});
		} finally {
			setIsExportingCsv(false);
		}
	};

	const handleSelectUpdateCsv = (event) => {
		const nextFile = event.target.files?.[0] || null;
		if (!nextFile) return;

		setCsvFile(nextFile);
		setCsvUploadSummary(null);
		setCsvModalOpen(true);
		setCsvFeedback(null);
	};

	const handleCloseCsvModal = () => {
		setCsvModalOpen(false);
		setCsvFile(null);
		setCsvUploadSummary(null);
		if (updateCsvInputRef.current) {
			updateCsvInputRef.current.value = "";
		}
	};

	const handleChangeCsvFile = () => {
		setCsvUploadSummary(null);
		if (updateCsvInputRef.current) {
			updateCsvInputRef.current.value = "";
		}
		updateCsvInputRef.current?.click();
	};

	const handleConfirmUpdateBulk = async () => {
		if (!csvFile) return;

		setIsUpdatingCsv(true);
		setCsvUploadSummary(null);

		try {
			const payload = new FormData();
			payload.append("file", csvFile);

			const response = await runUpdateBulkLicenses(payload);
			const updateResult = response?.message || {};

			if (
				typeof updateResult === "object" &&
				(updateResult.failedRows?.length > 0 || updateResult.failed > 0)
			) {
				const failedRows = updateResult.failedRows || [];
				setCsvUploadSummary({
					totalCount:
						updateResult.total ??
						(updateResult.failed ?? 0) + (updateResult.loaded ?? 0),
					loadedCount:
						updateResult.loaded ?? updateResult.updated ?? 0,
					failedCount: updateResult.failed ?? failedRows.length,
					failedRows: failedRows.map((r) => ({
						row: r?.row ?? null,
						serial: r?.serial ?? "",
						message: r?.error || r?.message || "Fila con error.",
					})),
				});
				return;
			}

			setCsvFeedback({
				type: "success",
				message: "Licencias actualizadas exitosamente mediante CSV.",
			});
			handleCloseCsvModal();
			await fetchLicenses(currentPage);
		} catch (error) {
			const errorMsg =
				error?.code === "LRT011"
					? error?.message ||
						"CSV no valido. Faltan encabezados requeridos."
					: error?.code === "LRL008"
						? "Su sesion ha expirado."
						: error?.message ||
							"Ocurrio un error al actualizar las licencias por CSV.";

			setCsvFeedback({ type: "error", message: errorMsg });
			setCsvUploadSummary({
				failedCount: 1,
				totalCount: 1,
				failedRows: [{ row: "-", message: errorMsg }],
			});
		} finally {
			setIsUpdatingCsv(false);
		}
	};

	return (
		<div className={styles.consultaWrapper}>
			<form onSubmit={handleSearch} className={styles.filterForm}>
				<div className={styles.inputsGrid}>
					<TextInput
						name="company"
						label="Empresa"
						placeholder="Ingrese la empresa"
						value={filters.company}
						onChange={(value) =>
							handleFilterChange({
								target: { name: "company", value },
							})
						}
						className={styles.fieldGroup}
						inputClassName={styles.fieldInput}
						hideFeedback
					/>

					<TextInput
						name="serial"
						label="Serial"
						placeholder="Ingrese el serial"
						value={filters.serial}
						onChange={(value) =>
							handleFilterChange({
								target: { name: "serial", value },
							})
						}
						className={styles.fieldGroup}
						inputClassName={styles.fieldInput}
						hideFeedback
					/>

					<SelectInput
						label="Modelo"
						name="deviceModel"
						value={filters.deviceModel}
						onChange={handleFilterChange}
						options={deviceModelOptions}
						disabled={isLoadingCatalogs}
						placeholder={
							isLoadingCatalogs
								? "Cargando modelos..."
								: "Todos los modelos"
						}
					/>

					<DateInput
						label="Fecha desde"
						name="startDate"
						value={filters.startDate}
						onChange={handleFilterChange}
					/>

					<DateInput
						label="Fecha hasta"
						name="endDate"
						value={filters.endDate}
						onChange={handleFilterChange}
					/>
				</div>

				<div className={styles.actionsRow}>
					<div className={styles.csvButtonsGroup}>
						<button
							type="button"
							className={styles.secondaryBtn}
							onClick={handleExportCsv}
							disabled={isExportingCsv}
						>
							{isExportingCsv
								? "Exportando..."
								: "Exportar todas a CSV"}
						</button>
						<button
							type="button"
							className={styles.secondaryBtn}
							onClick={() => updateCsvInputRef.current?.click()}
							disabled={isUpdatingCsv}
						>
							{isUpdatingCsv
								? "Actualizando..."
								: "Actualizar por CSV"}
						</button>
						<input
							ref={updateCsvInputRef}
							type="file"
							accept=".csv,text/csv"
							style={{ display: "none" }}
							onChange={handleSelectUpdateCsv}
						/>
					</div>

					<button
						type="submit"
						className={styles.searchBtn}
						disabled={isLoading}
					>
						{isLoading ? "Consultando..." : "Buscar licencias"}
					</button>
				</div>
			</form>

			{csvFeedback ? (
				<div
					className={`${styles.feedbackBanner} ${
						csvFeedback.type === "success"
							? styles.feedbackSuccess
							: styles.feedbackError
					}`}
					role="alert"
				>
					<span>{csvFeedback.message}</span>
					<button
						type="button"
						className={styles.feedbackCloseBtn}
						onClick={() => setCsvFeedback(null)}
						aria-label="Cerrar notificación"
					>
						×
					</button>
				</div>
			) : null}

			<div className={styles.tableSection}>
				<div className={styles.tableWrapper}>
					<table className={styles.resultsTable}>
						<thead>
							<tr>
								<th>Serial</th>
								<th>Empresa</th>
								<th>Compra</th>
								<th>Vencimiento</th>
								<th>Tipo</th>
								<th>Modelo</th>
								<th>Excepción</th>
								<th>Estado</th>
								<th>Licencia</th>
								<th>Copiar</th>
								<th>Modificar</th>
							</tr>
						</thead>
						<tbody>
							{lookupError ? (
								<tr>
									<td
										className={styles.emptyState}
										colSpan={11}
									>
										{lookupError}
									</td>
								</tr>
							) : licenseRows.length > 0 ? (
								licenseRows.map((row) => (
									<tr key={row._id || row.serial}>
										<td>{row.serial || "-"}</td>
										<td>{row.company || "-"}</td>
										<td>
											{row.purchaseDate
												? formatDate(row.purchaseDate)
												: "-"}
										</td>
										<td>
											{row.expirationDate
												? formatDate(row.expirationDate)
												: "-"}
										</td>
										<td>{row.type?.name || "-"}</td>
										<td>{row.deviceModel?.name || "-"}</td>
										<td>
											{row.exception
												? "Aplica"
												: "No aplica"}
										</td>
										<td>
											<span
												className={`${styles.statusBadge} ${
													row.status === "active"
														? styles.statusActive
														: row.status ===
															  "expired"
															? styles.statusExpired
															: styles.statusRevoked
												}`}
											>
												{row.status || "-"}
											</span>
										</td>
										<td>
											<span
												className={styles.licenseCell}
											>
												{row.license || "-"}
											</span>
										</td>
										<td className={styles.actionCell}>
											<button
												type="button"
												className={styles.copyButton}
												onClick={() =>
													handleCopyLicense(
														row.license,
														row.serial,
													)
												}
											>
												{copiedSerial === row.serial
													? "Copiado"
													: "Copiar"}
											</button>
										</td>
										<td className={styles.actionCell}>
											<button
												type="button"
												className={styles.copyButton}
												onClick={() =>
													handleModifyRow(row)
												}
											>
												Modificar
											</button>
										</td>
									</tr>
								))
							) : (
								<tr>
									<td
										className={styles.emptyState}
										colSpan={11}
									>
										Ingrese filtros y presione buscar para
										ver resultados.
									</td>
								</tr>
							)}
						</tbody>
					</table>
				</div>

				<div className={styles.paginationRow}>
					<div className={styles.paginationInfo}>
						Pagina {currentPage} de {totalPages} - {totalItems}{" "}
						registros
					</div>
					<div className={styles.paginationControls}>
						<button
							type="button"
							className={styles.pageBtn}
							onClick={() => goToPage(currentPage - 1)}
							disabled={currentPage <= 1 || isLoading}
						>
							Anterior
						</button>
						<span className={styles.activePageIndicator}>
							{currentPage}
						</span>
						<button
							type="button"
							className={styles.pageBtn}
							onClick={() => goToPage(currentPage + 1)}
							disabled={currentPage >= totalPages || isLoading}
						>
							Siguiente
						</button>
					</div>
				</div>
			</div>

			{editModalOpen ? (
				<Modal
					title="Modificar licencia"
					onClose={handleCloseEditModal}
					hideFooterButton
					hideIcon
				>
					<form
						onSubmit={handleConfirmEdit}
						className={styles.editModalForm}
					>
						<div className={styles.editFieldsGrid}>
							{Object.entries(editFieldLabels).map(
								([field, label]) => (
									<TextInput
										key={field}
										type={
											field === "email" ? "email" : "text"
										}
										name={field}
										label={label}
										value={editFormData[field]}
										onChange={(value) =>
											handleEditFieldChange({
												target: { name: field, value },
											})
										}
										maxLength={
											field === "serial" ? 11 : undefined
										}
										className={styles.fieldGroup}
										inputClassName={styles.fieldInput}
										hideFeedback
									/>
								),
							)}

							<TextInput
								label="Serial"
								value={editFormData.serial}
								readOnly
								className={styles.fieldGroup}
								inputClassName={styles.fieldInputReadOnly}
								hideFeedback
							/>

							<TextInput
								label="Tipo de licencia"
								value={buildLicenseTypeLabel(editFormData.type)}
								readOnly
								className={styles.fieldGroup}
								inputClassName={styles.fieldInputReadOnly}
								hideFeedback
							/>

							<SelectInput
								label="Modelo de dispositivo"
								name="deviceModel"
								value={editFormData.deviceModel}
								onChange={handleEditFieldChange}
								options={editDeviceModelOptions}
								placeholder="Seleccione un modelo"
							/>

							<SelectInput
								label="Excepción"
								name="exception"
								value={editFormData.exception}
								onChange={handleEditFieldChange}
								options={exceptionOptions}
								placeholder="Seleccione si aplica excepción"
								disabled={!isEditExceptionEnabled}
							/>

							<DateInput
								label="Fecha de compra"
								name="purchaseDate"
								value={editFormData.purchaseDate}
								onChange={handleEditFieldChange}
							/>

							<DateInput
								label="Fecha de vencimiento"
								name="expirationDate"
								value={editFormData.expirationDate}
								onChange={handleEditFieldChange}
							/>

							<SelectInput
								label="Estado"
								name="status"
								value={editFormData.status}
								onChange={handleEditFieldChange}
								options={statusOptions}
								placeholder="Seleccione un estado"
							/>
						</div>

						<div className={styles.editActions}>
							<button
								type="button"
								className={styles.editActionButton}
								onClick={handleCloseEditModal}
							>
								Cancelar
							</button>
							<button
								type="submit"
								className={`${styles.editActionButton} ${styles.editActionButtonPrimary}`}
							>
								Confirmar modificaciones
							</button>
						</div>
					</form>
				</Modal>
			) : null}

			{csvModalOpen && csvFile ? (
				<Modal
					title="Actualizar licencias por CSV"
					onClose={handleCloseCsvModal}
					hideFooterButton
					hideIcon
				>
					<div className={styles.csvModalContent}>
						<div className={styles.csvFileHero}>
							<div className={styles.csvFileBadge}>CSV</div>
							<div className={styles.csvFileInfo}>
								<span className={styles.csvFileLabel}>
									Archivo seleccionado
								</span>
								<strong className={styles.csvFileName}>
									{csvFile.name}
								</strong>
								<span className={styles.csvFileMeta}>
									{formatFileSize(csvFile.size)}
								</span>
							</div>
						</div>

						<p className={styles.csvModalIntro}>
							Se procesarán los registros del archivo para
							actualizar las licencias existentes en el sistema.
						</p>

						{csvUploadSummary ? (
							<div className={styles.csvSummaryBox}>
								<span className={styles.csvSummaryTitle}>
									Se encontraron inconsistencias (
									{csvUploadSummary.failedCount} de{" "}
									{csvUploadSummary.totalCount})
								</span>
								{csvUploadSummary.failedRows?.length > 0 ? (
									<div className={styles.csvSummaryList}>
										{csvUploadSummary.failedRows.map(
											(err, idx) => (
												<div
													key={`${err.serial || idx}-${idx}`}
													className={
														styles.csvSummaryItem
													}
												>
													{err.row ? (
														<span
															className={
																styles.csvSummaryRowTag
															}
														>
															Fila {err.row}:
														</span>
													) : null}
													<span>
														{err.serial
															? `[${err.serial}] `
															: ""}
														{err.message}
													</span>
												</div>
											),
										)}
									</div>
								) : null}
							</div>
						) : null}

						<div className={styles.csvModalActions}>
							<button
								type="button"
								className={styles.editActionButton}
								onClick={handleChangeCsvFile}
								disabled={isUpdatingCsv}
							>
								Cambiar archivo
							</button>
							<button
								type="button"
								className={styles.editActionButton}
								onClick={handleCloseCsvModal}
								disabled={isUpdatingCsv}
							>
								Cancelar
							</button>
							<button
								type="button"
								className={`${styles.editActionButton} ${styles.editActionButtonPrimary}`}
								onClick={handleConfirmUpdateBulk}
								disabled={isUpdatingCsv}
							>
								{isUpdatingCsv
									? "Actualizando..."
									: "Actualizar licencias"}
							</button>
						</div>
					</div>
				</Modal>
			) : null}
		</div>
	);
};

export default Search;
